import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { trackerKeys } from './hooks';
import type { SupabaseClient } from '../../lib/supabase';
import type { Tables } from '../../types/database.types';

/**
 * What a lead carries beyond its status: Direction, Source, Categories and
 * Nurture — the fields from decision 3 of the restructure.
 *
 * A module of its own rather than another thousand lines in TrackerPage.tsx,
 * which is 4,993 lines holding nine tabs. The restructure doc names that file
 * as an obstacle before it names anything else; the way out of it is to stop
 * adding to it, one surface at a time, starting with the ones this work
 * touches.
 *
 * ── The three vocabularies ─────────────────────────────────────────────────
 *
 * Sources, Categories and Closure reasons are rows, not enums, because Kyu
 * will add to them: "You will almost certainly add categories, sources, or
 * closure reasons later. Do not require a developer to change a dropdown."
 *
 * Every read here asks only for ACTIVE rows, because the rule that comes with
 * that one is "never delete an option already used on records; deactivate it
 * instead". A retired value must keep rendering on the leads that already
 * carry it while disappearing from the pickers — so the pickers filter and
 * the lookups do not.
 */

export type LeadSource = Tables<'lead_sources'>;
export type LeadCategory = Tables<'lead_categories'>;
export type LeadClosureReason = Tables<'lead_closure_reasons'>;
export type LeadCategoryLink = Tables<'lead_category_links'>;
export type LeadCaptureMethod = Tables<'lead_capture_methods'>;

const VOCAB_COLUMNS = 'id, name, position, is_active, created_at';

function fail(message: string): never {
  throw new Error(message);
}

export const leadFieldKeys = {
  sources: () => [...trackerKeys.all, 'lead-sources'] as const,
  categories: () => [...trackerKeys.all, 'lead-categories'] as const,
  closureReasons: () => [...trackerKeys.all, 'lead-closure-reasons'] as const,
  categoryLinks: () => [...trackerKeys.all, 'lead-category-links'] as const,
  captureMethods: () => [...trackerKeys.all, 'lead-capture-methods'] as const,
};

async function fetchVocabulary<T>(
  client: SupabaseClient,
  table: 'lead_sources' | 'lead_categories' | 'lead_closure_reasons',
): Promise<T[]> {
  const { data, error } = await client
    .from(table)
    .select(VOCAB_COLUMNS)
    .order('position', { ascending: true });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? (data as unknown as T[]) : [];
}

/**
 * Lead Origin carries one column the other two vocabularies do not —
 * `applies_to`, saying whether a value belongs to the inbound side, the
 * outbound side, or both. So it gets its own select rather than widening the
 * shared one, which would ask lead_categories and lead_closure_reasons for a
 * column they do not have.
 */
const ORIGIN_COLUMNS = 'id, name, position, is_active, created_at, applies_to, group_name';

export async function fetchLeadSources(client: SupabaseClient): Promise<LeadSource[]> {
  const { data, error } = await client
    .from('lead_sources')
    .select(ORIGIN_COLUMNS)
    .order('position', { ascending: true });
  if (error) {
    fail(error.message);
  }
  // No cast: database.types.ts now carries applies_to, so the row this select
  // returns IS a LeadSource. The generic fetcher above still needs its cast —
  // it selects a narrower column list than its T.
  return Array.isArray(data) ? data : [];
}

/**
 * The Lead Origin values worth offering for a given Lead Source.
 *
 * Two filters answering different questions. `offerable` drops values retired
 * for new use — the legacy `LinkedIn`, after Alex split it into Ads and
 * Outreach. This drops values belonging to the other side.
 *
 * ── Why the current value survives both filters ────────────────────────────
 *
 * Whatever is already selected stays in the list even when it is retired or
 * belongs to the other side. A Select whose value is missing from its options
 * renders blank, so filtering the chosen value out would make somebody's entry
 * silently vanish the moment they changed Lead Source. Keeping it visible is
 * the rule this codebase already applies to retired values on the leads that
 * carry them: never make a stored fact disappear from the screen.
 */
export function originsFor(
  rows: LeadSource[],
  leadSource: 'inbound' | 'outbound' | null,
  currentId: string | null,
): LeadSource[] {
  const live = offerable(rows);
  const scoped =
    leadSource === null
      ? live
      : live.filter((row) => row.applies_to === 'both' || row.applies_to === leadSource);
  if (currentId === null || scoped.some((row) => row.id === currentId)) {
    return scoped;
  }
  const held = rows.find((row) => row.id === currentId);
  return held === undefined ? scoped : [...scoped, held];
}

/** True when the chosen origin does not belong to the chosen side. */
export function originMismatch(
  rows: LeadSource[],
  leadSource: 'inbound' | 'outbound' | null,
  currentId: string | null,
): boolean {
  if (leadSource === null || currentId === null) {
    return false;
  }
  const held = rows.find((row) => row.id === currentId);
  if (held === undefined) {
    return false;
  }
  return held.applies_to !== 'both' && held.applies_to !== leadSource;
}

/**
 * How a source reads in a picker: "Social media > Instagram".
 *
 * Alex's own notation, rendered by prefixing the group rather than by teaching
 * the shared Select about optgroups. One string, no new UI primitive, and the
 * label on screen is literally what he wrote.
 *
 * A value whose name IS its group renders once. `Social Media` sits in the
 * Social media group as the answer for a lead where nobody knows which channel
 * it was, and "Social media > Social Media" would read like a mistake.
 */
export function originLabel(row: Pick<LeadSource, 'name' | 'group_name'>): string {
  const group = (row.group_name ?? '').trim();
  const name = String(row.name);
  if (group === '' || group.toLowerCase() === name.toLowerCase()) {
    return name;
  }
  return `${group} › ${name}`;
}

// ── How their details reached us ───────────────────────────────────────────

/**
 * The other half of the question, and a separate field on purpose.
 *
 * Alex: "Social media > Instagram" is the SOURCE, "ManyChat DM" is the CAPTURE
 * METHOD. They are independent — an Instagram lead can arrive by a ManyChat
 * DM, by clicking through to a website form, or by emailing us — and the Hub
 * could record only one of the three until now.
 *
 * Scoped by direction the same way origins are, so an Imported list is not
 * offered on an inbound lead. Same rule about the held value surviving the
 * filter: a stored fact never disappears from the screen.
 */
export function capturesFor(
  rows: LeadCaptureMethod[],
  leadSource: 'inbound' | 'outbound' | null,
  currentId: string | null,
): LeadCaptureMethod[] {
  const live = offerable(rows);
  const scoped =
    leadSource === null
      ? live
      : live.filter((row) => row.applies_to === 'both' || row.applies_to === leadSource);
  if (currentId === null || scoped.some((row) => row.id === currentId)) {
    return scoped;
  }
  const held = rows.find((row) => row.id === currentId);
  return held === undefined ? scoped : [...scoped, held];
}

const CAPTURE_COLUMNS = 'id, name, position, is_active, created_at, applies_to';

export async function fetchLeadCaptureMethods(
  client: SupabaseClient,
): Promise<LeadCaptureMethod[]> {
  const { data, error } = await client
    .from('lead_capture_methods')
    .select(CAPTURE_COLUMNS)
    .order('position', { ascending: true });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export function useLeadCaptureMethods() {
  const { client } = useAuth();
  return useQuery({
    queryKey: leadFieldKeys.captureMethods(),
    queryFn: () => fetchLeadCaptureMethods(client),
    staleTime: 5 * 60_000,
  });
}

export async function fetchLeadCategories(client: SupabaseClient): Promise<LeadCategory[]> {
  return fetchVocabulary<LeadCategory>(client, 'lead_categories');
}

/**
 * Closure reasons carry one column the other two vocabularies do not —
 * `blocks_contact` (20261219000100), which is what keeps a lead who asked us
 * to stop out of a campaign list. So this gets its own select, exactly like
 * ORIGIN_COLUMNS above and for the same reason: widening the shared one would
 * ask lead_categories and lead_sources for a column they do not have, and
 * PostgREST rejects the whole request for an unknown column.
 *
 * SHIPPED WRONG ONCE, in Drop 38, and it is worth saying why it got through.
 * The guard read `reason.blocks_contact` off rows this select never asked for,
 * so the value was `undefined` at runtime while TypeScript believed it was a
 * boolean — the exact failure the LEAD_COLUMNS comment describes, one file
 * along, in the safety check itself. Every unit test passed, because the
 * fixtures set a field the wire never carried. leadColumns.test.ts now checks
 * this select too.
 */
const CLOSURE_COLUMNS = 'id, name, position, is_active, created_at, blocks_contact';

export async function fetchClosureReasons(client: SupabaseClient): Promise<LeadClosureReason[]> {
  const { data, error } = await client
    .from('lead_closure_reasons')
    .select(CLOSURE_COLUMNS)
    .order('position', { ascending: true });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export async function fetchCategoryLinks(client: SupabaseClient): Promise<LeadCategoryLink[]> {
  const { data, error } = await client
    .from('lead_category_links')
    .select('lead_id, category_id, created_at');
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

/**
 * Only what a picker should offer.
 *
 * Retired values stay readable on the leads that carry them — which is the
 * whole reason deactivating exists instead of deleting — so this filter
 * belongs on the dropdown and nowhere near the lookup.
 */
export function offerable<T extends { is_active: boolean }>(rows: T[]): T[] {
  return rows.filter((row) => row.is_active);
}

/** Category ids for one lead, from the link rows. */
export function categoriesOf(leadId: string, links: LeadCategoryLink[]): string[] {
  return links.filter((link) => link.lead_id === leadId).map((link) => link.category_id);
}

/** Name for an id, from any of the three vocabularies. '' when unknown. */
export function nameOf(id: string | null, rows: { id: string; name: string }[]): string {
  if (id === null) {
    return '';
  }
  return rows.find((row) => row.id === id)?.name ?? '';
}

export function useLeadSources() {
  const { client } = useAuth();
  return useQuery({
    queryKey: leadFieldKeys.sources(),
    queryFn: () => fetchLeadSources(client),
    staleTime: 5 * 60_000,
  });
}

export function useLeadCategories() {
  const { client } = useAuth();
  return useQuery({
    queryKey: leadFieldKeys.categories(),
    queryFn: () => fetchLeadCategories(client),
    staleTime: 5 * 60_000,
  });
}

export function useClosureReasons() {
  const { client } = useAuth();
  return useQuery({
    queryKey: leadFieldKeys.closureReasons(),
    queryFn: () => fetchClosureReasons(client),
    staleTime: 5 * 60_000,
  });
}

export function useCategoryLinks() {
  const { client } = useAuth();
  return useQuery({
    queryKey: leadFieldKeys.categoryLinks(),
    queryFn: () => fetchCategoryLinks(client),
  });
}

/**
 * Set a lead's categories to exactly this set.
 *
 * Delete-then-insert rather than a diff, because the set is at most a handful
 * and a diff is a second place for the two to disagree. The delete is scoped
 * to the one lead, so nothing else can be caught by it.
 */
export function useSetLeadCategories() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { leadId: string; categoryIds: string[] }) => {
      const { error: clearError } = await client
        .from('lead_category_links')
        .delete()
        .eq('lead_id', input.leadId);
      if (clearError) {
        throw new Error(clearError.message);
      }
      if (input.categoryIds.length === 0) {
        return;
      }
      const { error } = await client.from('lead_category_links').insert(
        input.categoryIds.map((categoryId) => ({
          lead_id: input.leadId,
          category_id: categoryId,
        })),
      );
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: leadFieldKeys.categoryLinks() });
    },
  });
}

/** Categories for a whole imported batch, in one insert. */
export function useSetCategoriesForMany() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { leadIds: string[]; categoryIds: string[] }) => {
      if (input.leadIds.length === 0 || input.categoryIds.length === 0) {
        return;
      }
      const rows = input.leadIds.flatMap((leadId) =>
        input.categoryIds.map((categoryId) => ({ lead_id: leadId, category_id: categoryId })),
      );
      const { error } = await client.from('lead_category_links').insert(rows);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: leadFieldKeys.categoryLinks() });
    },
  });
}

/**
 * Nurture on or off, with a note.
 *
 * The since-date went in batch 49. Nothing read it but its own badge, it was
 * null for every lead predating the restructure, and the timeline already
 * records when nurture was switched on and by whom — which is the same fact
 * with a name attached. `nurture_since` is left alone in the database rather
 * than cleared: removing a column's UI is reversible, rewriting its data is
 * not.
 *
 * Kyu: "Nurture is a relationship with the person/company, not a particular
 * Deal." Turning it OFF clears the note, because a lead that is not in nurture
 * should not carry a nurture story.
 */
export function useSetNurture() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { leadId: string; active: boolean; note?: string }) => {
      const { error } = await client
        .from('leads')
        .update(
          input.active
            ? { nurture_active: true, nurture_note: input.note ?? '' }
            : { nurture_active: false, nurture_note: '' },
        )
        .eq('id', input.leadId);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: trackerKeys.leads() });
    },
  });
}

/**
 * Correct a Direction that was set wrong, with a reason.
 *
 * The RPC is the only door: inbound is permanent on every ordinary path
 * because it is a consent record. See 20261122000100 — a TAP admin, a reason,
 * and an audit line in sales_activity carrying their name.
 */
export function useCorrectLeadDirection() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      leadId: string;
      direction: 'inbound' | 'outbound';
      reason: string;
    }) => {
      const { error } = await client.rpc('correct_lead_direction', {
        p_lead: input.leadId,
        p_direction: input.direction,
        p_reason: input.reason,
      });
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: trackerKeys.leads() });
    },
  });
}

/**
 * Mark several leads as contacted in one pass.
 *
 * For the leads Nez emailed from Gmail where nothing was ever recorded. The
 * Hub holds no evidence of those, and a migration that marked them Contacted
 * would be inventing a fact — so the remedy is a person saying so, once, with
 * their name on it.
 *
 * Only moves leads that are still on New. Somebody selecting a whole page and
 * pressing this must not drag an Engaged lead backwards, and the status guard
 * would not stop them: New → Contacted is forward, but Engaged → Contacted is
 * not, and this is a bulk action where the mistake is invisible.
 *
 * Batch 2 makes each of these write a timeline activity marked "recorded
 * retrospectively", so nobody later reads today's date as the day it was
 * actually sent. Until then the status move IS the record.
 */
export function useMarkContacted() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      leadIds: string[];
      newStageId: string;
      contactedStageId: string;
    }): Promise<number> => {
      if (input.leadIds.length === 0) {
        return 0;
      }
      const { data, error } = await client
        .from('leads')
        .update({ stage_id: input.contactedStageId })
        .in('id', input.leadIds)
        .eq('stage_id', input.newStageId)
        .select('id');
      if (error) {
        throw new Error(error.message);
      }
      return Array.isArray(data) ? data.length : 0;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: trackerKeys.leads() });
    },
  });
}

/** The database's refusals, in words somebody can act on. */
export function leadFieldErrorMessage(raw: string): string {
  if (raw.includes('closure_reason_required')) {
    return 'Say why this lead is closed before saving it.';
  }
  if (raw.includes('direction_correction_needs_admin')) {
    return 'Only a TAP admin can correct a lead’s direction.';
  }
  if (raw.includes('direction_correction_reason_required')) {
    return 'Give a reason for the correction. It is written to the lead’s history with your name.';
  }
  if (raw.includes('direction_unchanged')) {
    return 'That is already the direction on this lead.';
  }
  if (raw.includes('lead_consent_is_permanent')) {
    // Was "Inbound is a consent record…". The guard covers both directions
    // now — outbound → inbound manufactures a consent claim and is refused
    // too — so a message naming only Inbound would be wrong half the time.
    return 'Lead Source is recorded once. A TAP admin can correct it with a reason, which is written to the lead’s history.';
  }
  if (raw.includes('violates foreign key constraint')) {
    return 'That option is still in use on some leads, so it cannot be deleted. Deactivate it instead.';
  }
  return 'That could not be saved. Try again.';
}
